import { useState } from 'react'
import './GameRoomFooter.css'

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'failed' | 'closed'

export interface VoiceStatus {
  localMuted: boolean
  remoteMuted: boolean
  localConnected: boolean
  remoteConnected: boolean
}

export interface GameRoomFooterProps {
  voice?: {
    connectionStatus: ConnectionStatus
    voiceStatus: VoiceStatus
    onToggleMute: () => void
    onConnect?: () => void
    onDisconnect?: () => void
  }
  mode?: 'minimal' | 'expanded'
  expandable?: boolean
  onToggleExpand?: () => void
}

export function GameRoomFooter({
  voice,
  mode = 'minimal',
  expandable = true,
  onToggleExpand
}: GameRoomFooterProps) {
  const [isExpanded, setIsExpanded] = useState(mode === 'expanded')

  if (!voice) {
    return null // No footer if no voice controls
  }

  const { connectionStatus, voiceStatus, onToggleMute, onConnect, onDisconnect } = voice

  const handleToggleExpand = () => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)
    onToggleExpand?.()
  }

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'var(--success)'
      case 'connecting': return 'var(--warning)'
      case 'disconnected':
      case 'failed':
      case 'closed':
        return 'var(--error)'
      default: return 'var(--text-secondary)'
    }
  }

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return voiceStatus.remoteConnected
          ? '✅ Both connected'
          : '⏳ Waiting for other player...'
      case 'connecting':
        return '⏳ Connecting...'
      case 'disconnected':
        return 'Voice Off'
      case 'failed':
        return '✕ Connection Failed'
      case 'closed':
        return 'Call Ended'
      default:
        return 'Voice Chat'
    }
  }

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return '●'
      case 'connecting': return '⏳'
      default: return '○'
    }
  }

  // Minimal Mode (Default)
  if (!isExpanded) {
    return (
      <footer className="game-room-footer minimal">
        <button
          className="status-button"
          onClick={expandable ? handleToggleExpand : undefined}
          style={{ color: getStatusColor() }}
          aria-label={`Voice status: ${getStatusText()}`}
        >
          <span className="status-icon">{getStatusIcon()}</span>
          <span className="status-text">{getStatusText()}</span>
          {expandable && <span className="expand-icon">⬆️</span>}
        </button>

        <div className="controls-row">
          <button
            className={`control-button mute-button ${voiceStatus.localMuted ? 'muted' : ''}`}
            onClick={onToggleMute}
            aria-label={voiceStatus.localMuted ? 'Unmute' : 'Mute'}
            disabled={connectionStatus !== 'connected'}
          >
            {voiceStatus.localMuted ? '🔇' : '🎤'}
            <span className="button-label">
              {voiceStatus.localMuted ? 'Unmute' : 'Mute'}
            </span>
          </button>

          {connectionStatus === 'connected' && onDisconnect && (
            <button
              className="control-button disconnect-button"
              onClick={onDisconnect}
              aria-label="End call"
            >
              📞
              <span className="button-label">End Call</span>
            </button>
          )}

          {connectionStatus !== 'connected' && onConnect && (
            <button
              className="control-button connect-button"
              onClick={onConnect}
              aria-label="Connect voice"
            >
              📞
              <span className="button-label">Connect</span>
            </button>
          )}
        </div>
      </footer>
    )
  }

  // Expanded Mode
  return (
    <footer className="game-room-footer expanded">
      <div className="footer-header">
        <h3 className="footer-title">Voice Call</h3>
        {expandable && (
          <button
            className="collapse-button"
            onClick={handleToggleExpand}
            aria-label="Collapse"
          >
            ⬇️
          </button>
        )}
      </div>

      <div className="footer-content">
        <div className="status-display">
          <div
            className="status-indicator-large"
            style={{ backgroundColor: getStatusColor() }}
          />
          <div className="status-info">
            <span className="status-label">{getStatusText()}</span>
            {connectionStatus === 'connecting' && (
              <div className="spinner" />
            )}
          </div>
        </div>

        <div className="controls-grid">
          <button
            className={`control-button-large ${voiceStatus.localMuted ? 'muted' : ''}`}
            onClick={onToggleMute}
            disabled={connectionStatus !== 'connected'}
          >
            <span className="button-icon">
              {voiceStatus.localMuted ? '🔇' : '🎤'}
            </span>
            <span className="button-text">
              {voiceStatus.localMuted ? 'Unmute' : 'Mute'}
            </span>
          </button>

          {connectionStatus === 'connected' && onDisconnect && (
            <button
              className="control-button-large disconnect"
              onClick={onDisconnect}
            >
              <span className="button-icon">📞</span>
              <span className="button-text">End Call</span>
            </button>
          )}

          {connectionStatus !== 'connected' && onConnect && (
            <button
              className="control-button-large connect"
              onClick={onConnect}
            >
              <span className="button-icon">📞</span>
              <span className="button-text">Connect</span>
            </button>
          )}
        </div>

        {connectionStatus === 'failed' && (
          <div className="error-message">
            <p>Unable to establish voice connection.</p>
            <small>Check your microphone permissions and try again.</small>
          </div>
        )}
      </div>
    </footer>
  )
}
